import { useState } from 'react'
import type { WizardStepProps, ComponentType } from '../../../../types/builder.types'

import QueryBuilder from '@/components/generic/QueryBuilder' 

const COMPONENT_TYPES = [
  // Componentes originales
  {
    type: 'kpi' as ComponentType,
    icon: '📊',
    label: 'KPI',
    description: 'Indicador clave de rendimiento con agregación (suma, promedio, etc.)',
    category: 'Básicos'
  },
  {
    type: 'line-chart' as ComponentType,
    icon: '📈',
    label: 'Gráfico de Líneas',
    description: 'Perfecto para mostrar tendencias y evolución temporal',
    category: 'Básicos'
  },
  {
    type: 'bar-chart' as ComponentType,
    icon: '📊',
    label: 'Gráfico de Barras',
    description: 'Ideal para comparar categorías o municipios',
    category: 'Básicos'
  },
  {
    type: 'table' as ComponentType,
    icon: '📋',
    label: 'Tabla',
    description: 'Listado interactivo con ordenamiento y paginación',
    category: 'Básicos'
  },

  // Fase 1: Componentes críticos
  {
    type: 'map' as ComponentType,
    icon: '🗺️',
    label: 'Mapa Interactivo',
    description: 'Visualiza distribución geográfica con coordenadas GPS',
    category: 'Geográficos'
  },
  {
    type: 'pie-chart' as ComponentType,
    icon: '🥧',
    label: 'Gráfico Circular',
    description: 'Muestra distribución porcentual de categorías (pie o donut)',
    category: 'Distribución'
  },
  {
    type: 'gauge' as ComponentType,
    icon: '🎯',
    label: 'Medidor/Gauge',
    description: 'Indicador visual con umbrales (bajo/medio/alto)',
    category: 'Indicadores'
  },

  // Fase 2: Componentes muy útiles
  {
    type: 'area-chart' as ComponentType,
    icon: '📈',
    label: 'Gráfico de Área',
    description: 'Evolución temporal con múltiples series apiladas',
    category: 'Tendencias'
  },
  {
    type: 'stacked-bar' as ComponentType,
    icon: '📊',
    label: 'Barras Apiladas',
    description: 'Comparación de composición entre categorías',
    category: 'Comparación'
  },
  {
    type: 'scatter-plot' as ComponentType,
    icon: '🔵',
    label: 'Gráfico de Dispersión',
    description: 'Relación entre 2-3 variables con burbujas opcionales',
    category: 'Análisis'
  }, 

  // Fase 3: Nice to have
  {
    type: 'heatmap' as ComponentType,
    icon: '🔥',
    label: 'Mapa de Calor',
    description: 'Matriz de intensidad (ej: provincia × especie)',
    category: 'Análisis'
  },
  {
    type: 'treemap' as ComponentType,
    icon: '🌳',
    label: 'Mapa de Árbol',
    description: 'Proporciones jerárquicas con rectángulos anidados',
    category: 'Distribución'
  },
  {
    type: 'radar-chart' as ComponentType,
    icon: '🎯',
    label: 'Gráfico Radial',
    description: 'Comparación multidimensional (5-6 métricas)',
    category: 'Comparación'
  },
  {
    type: 'radial-bar' as ComponentType,
    icon: '🔵',
    label: 'Barras Radiales',
    description: 'Progreso hacia objetivos en forma circular',
    category: 'Indicadores'
  },

  // CKAN: Datos externos
  {
    type: 'forest-associations-table' as ComponentType,
    icon: '🌲',
    label: 'Asociaciones Forestales',
    description: 'Tabla de asociaciones forestales de CyL (fuente: CKAN)',
    category: 'Datos CKAN'
  }
]

export function ComponentTypeStep({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const handleSelect = (type: ComponentType) => {
    onChange({ ...config, componentType: type });
  };

  const [hasShownQueryBuilder, setHasShownQueryBuilder] = useState(false);

   const handleSaveConfig = (queryConfig: any, sql?: any) => {
    console.log("Configuración guardada:", queryConfig);
    
    // VALIDAR que no sea un evento
    if (queryConfig && queryConfig.target && queryConfig.type === 'click') {
      console.error("ERROR: Se recibió un evento en lugar de configuración");
      return;
    }
    
    // Actualiza el estado global
      onChange({
        ...config,
        customQuery: {
          ...config.customQuery,
        sql: sql,
        config: queryConfig
      }
    });
  };

    const handleSaveSql = (sql: string) => {
    console.log("SQL guardado:", sql);
    // Actualiza el estado global
    onChange({
      ...config,
      customQuery: {
        ...config.customQuery,
        sql: sql
      }
    });
  };

  const handleSaved = () => {
    console.log("Todo guardado en QueryBuilder");
    onChange({
      ...config,
      customQuery: {
        enabled: true,
        sql: config.customQuery?.sql || "",
        config: config.customQuery?.config || {},
        saved: true
      }
    });

    
    setHasShownQueryBuilder(true);
  };

  

  // Agrupar componentes por categoría
  const categories = Array.from(new Set(COMPONENT_TYPES.map(t => t.category)))


   const shouldShowQueryBuilder = config.dataSource === 'custom_query' && 
    !hasShownQueryBuilder;


   if (shouldShowQueryBuilder) {
    return (
      <div className="max-w-6xl mx-auto">
        <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
          ← Volver
        </button>

        <h2 className="text-2xl font-bold mb-2">Configurar Consulta Personalizada</h2>
        <p className="text-gray-600 mb-8">
          Construye tu consulta SQL personalizada. Cuando termines, podrás seleccionar el tipo de componente.
        </p>
        <QueryBuilder
            //initialConfig={config.customQuery?.config}
             initialConfig={config?.customQuery?.config ? {
                baseTable: config.customQuery.config.baseTable || '',
                selectedFields: config.customQuery.config.selectedFields || [],
                joins: (config.customQuery.config.joins || []) as any[],
                filters: (config.customQuery.config.filters || []) as any[],
                groupByFields: config.customQuery.config.groupByFields || [],
                limit: config.customQuery.config.limit ?? 5000,
                offset: config.customQuery.config.offset || 0,
                agreggation: config.customQuery.config.agreggation || [],
                fieldAliases: config.customQuery.config.fieldAliases || undefined,
                computedColumnExpressions: config.customQuery.config.computedColumnExpressions || undefined,
                orderByRaw: config.customQuery.config.orderByRaw || undefined,
              } : undefined}
            initialSql={config.customQuery?.sql}
            onSaveConfig={handleSaveConfig}
            onSaveSql={handleSaveSql}
            onSaved={handleSaved}
          />

        {/* Botón para continuar manualmente si es necesario */}
        {config.customQuery?.saved && (
          <div className="mt-8 pt-6 border-t">
            <button
              onClick={() => setHasShownQueryBuilder(true)}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Continuar para Elegir Tipo de Componente →
            </button>
          </div>
        )}
      </div>
    );
  }

  


  return (
    <div className="max-w-6xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Paso 2: Tipo de Componente</h2>
      <p className="text-gray-600 mb-8">
        Selecciona qué tipo de visualización deseas crear ({COMPONENT_TYPES.length} tipos disponibles)
      </p>

      {/* Componentes agrupados por categoría */}
      {categories.map((category) => {
        const items = COMPONENT_TYPES.filter(t => t.category === category)
        return (
          <div key={category} className="mb-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-600 rounded-full"></span>
              {category}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => (
                <button
                  key={item.type}
                  onClick={() => handleSelect(item.type)}
                  className={`
                    text-left border-2 rounded-lg p-4 transition-all hover:shadow-md
                    ${
                      config.componentType === item.type
                        ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                        : 'border-gray-200 hover:border-green-300'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-4xl">{item.icon}</span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-base mb-1">{item.label}</h4>
                      <p className="text-gray-600 text-sm line-clamp-2">{item.description}</p>
                    </div>
                    {config.componentType === item.type && (
                      <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )
      })}

      <div className="sticky bottom-0 bg-white border-t border-gray-200 pt-6 mt-8">
        <button
          onClick={onNext}
          disabled={!config.componentType}
          className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Continuar con la Configuración →
        </button>
      </div>
    </div>
  )
}
